import { AutopilotTaskDto, TaskStepDto } from '@urbanx/agx.activities.client';
import {
  AgxCollapsible,
  AgxEmptyState,
  AgxIconTask,
  AgxProgressBar,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import { ReactNode, useRef, useState } from 'react';
import {
  getMissingDueDateText,
  getStepCompletionPercentage,
  getStepsCompletionInfo,
  getTaskDomId,
  getTaskLabel,
} from 'utils/tasks';
import TaskAssigneeSelect from './TaskAssigneeSelect';
import TaskDueDateBadge from './TaskDueDateBadge';
import TaskStepCompletionConfirmationModal from './TaskStepCompletionConfirmationModal';
import TaskStepItem from './TaskStepItem';
import {
  AssigneeOption,
  AssignTaskHandler,
  mapTaskTypeToIconType,
  ToggleTaskStepsHandler,
} from './taskCollapsibleShared';
import {
  getTaskStepCompletionConfirmationVariant,
  type TaskStepCompletionConfirmationVariant,
} from './taskStepConfirmation';

type TaskCollapsiblePaddingPreset = 'default' | 'workflowTask';

interface TaskCollapsibleProps {
  task: AutopilotTaskDto;
  tasks: AutopilotTaskDto[];
  assigneeOptions: AssigneeOption[];
  assignTask: AssignTaskHandler;
  toggleTaskSteps: ToggleTaskStepsHandler;
  paddingPreset?: TaskCollapsiblePaddingPreset;
  renderTaskContent?: (props: { task: AutopilotTaskDto; children: ReactNode }) => ReactNode;
}

type PendingStepConfirmation = {
  step: TaskStepDto;
  variant: TaskStepCompletionConfirmationVariant;
};

const TaskCollapsible = ({
  task,
  tasks,
  assigneeOptions,
  assignTask,
  toggleTaskSteps,
  paddingPreset = 'default',
  renderTaskContent,
}: TaskCollapsibleProps) => {
  const [pendingStep, setPendingStep] = useState<PendingStepConfirmation | undefined>();

  const savingStepsRef = useRef(new Set<number>());
  const [savingSteps, setSavingSteps] = useState(new Set<number>());

  const saveStep = (step: TaskStepDto) => {
    const order = step.order!;
    if (savingStepsRef.current.has(order)) return;

    savingStepsRef.current.add(order);
    setSavingSteps(new Set(savingStepsRef.current));
    const finishSaving = () => {
      savingStepsRef.current.delete(order);
      setSavingSteps(new Set(savingStepsRef.current));
    };
    // The mutation owner handles errors; release this step on either outcome.
    void toggleTaskSteps({ taskId: task.id, stepIds: [order] }).then(finishSaving, finishSaving);
  };

  if (!task.category) return null;

  const taskSteps = task.taskSteps ?? [];
  const { allStepsCompleted, latestCompletedDate } = getStepsCompletionInfo([task]);

  const isUnassigned = !task.isPending && (!task.assignedTo || task.assignedTo === 'Unassigned');

  const assigneeSelectId = getTaskDomId('assignToUserChip', task);

  const toggleStep = (step: TaskStepDto) => {
    if (savingStepsRef.current.has(step.order!)) return;

    const confirmationVariant = getTaskStepCompletionConfirmationVariant({ task, tasks, step });

    if (confirmationVariant) {
      setPendingStep({ step, variant: confirmationVariant });
      return;
    }

    saveStep(step);
  };

  const confirmPendingStep = () => {
    if (!pendingStep) return;

    saveStep(pendingStep.step);
    setPendingStep(undefined);
  };

  const closePendingStepConfirmation = () => setPendingStep(undefined);
  const usesWorkflowTaskPadding = paddingPreset === 'workflowTask';
  const hasBlockers = (task.blockers?.length ?? 0) > 0;

  const content = isUnassigned ? (
    <AgxSpacer direction="horizontal" fill contentAlignment="center" contentJustification="center">
      <AgxEmptyState
        variant="advanced"
        glyphType="locked"
        actionButtons={[]}
        label="Assign task to unlock"
      />
    </AgxSpacer>
  ) : (
    <AgxSpacer direction="vertical" fill gap="spacing-lg">
      {taskSteps.map((step) => (
        <TaskStepItem
          key={`${step.order}-${step.name}`}
          step={step}
          allStepsCompleted={allStepsCompleted}
          isSaving={savingSteps.has(step.order!)}
          taskType={task.category?.taskType}
          onToggleStep={toggleStep}
        />
      ))}
    </AgxSpacer>
  );

  return (
    <div data-testid={getTaskDomId('agx-task-card', task)}>
      <AgxCollapsible
        key={task.id}
        className="taskCollapsible"
        open={!task.isCompleted}
        iconPosition="none"
        enableToggle={true}
        smoothTransition
        variant={'withoutDivider'}
        title={''}
        headerPadding={
          usesWorkflowTaskPadding ? { top: 'md', bottom: 'md', left: 'xl', right: 'xl' } : undefined
        }
        contentPadding={
          usesWorkflowTaskPadding && hasBlockers
            ? { top: '2xl', bottom: '2xl', left: 'xl', right: 'xl' }
            : { left: 'xl', bottom: 'xl', right: 'xl' }
        }
        customTitle={
          <AgxSpacer direction="vertical" fill gap="spacing-ml">
            <AgxSpacer
              direction="horizontal"
              fill
              contentAlignment="center"
              contentJustification="space-between"
            >
              <AgxSpacer direction="horizontal" gap="spacing-lg" contentAlignment="center">
                <AgxIconTask
                  type={mapTaskTypeToIconType(task.category?.taskType)}
                  iconColor="black"
                  height={32}
                  width={32}
                  bgColor="var(--bg-tag-grey)"
                />
                <AgxTypography id={'title'} type={'heading'} size={'medium'}>
                  {getTaskLabel(task)}
                </AgxTypography>
              </AgxSpacer>
              <AgxSpacer
                direction="horizontal"
                gap="spacing-lg"
                contentJustification="end"
                contentAlignment="center"
              >
                <div onClick={(e) => e.stopPropagation()}>
                  {!allStepsCompleted ? (
                    <TaskAssigneeSelect
                      id={assigneeSelectId}
                      value={task.assignedTo}
                      onAssign={(userId) => {
                        if (!task.id) return;

                        assignTask({
                          taskId: task.id,
                          userId,
                        });
                      }}
                      options={assigneeOptions}
                    />
                  ) : (
                    <AgxTypography
                      id={`${assigneeSelectId}-completed`}
                      type="caption"
                      size="medium"
                    >
                      {assigneeOptions.find((o) => o.value === task.assignedTo)?.label ??
                        task.assignedTo}
                    </AgxTypography>
                  )}
                </div>
                <TaskDueDateBadge
                  dueDate={(task.dueUtc ?? '') as string}
                  isCompleted={allStepsCompleted}
                  completedDate={latestCompletedDate}
                  missingDateText={getMissingDueDateText(task)}
                />
              </AgxSpacer>
            </AgxSpacer>
            {task.isCompleted ? (
              <AgxTypography
                id={'txtComplete'}
                type={'caption'}
                size={'medium'}
                color="var(--text-success)"
              >
                Complete! 🎉
              </AgxTypography>
            ) : (
              <AgxProgressBar
                variant="slim"
                id="progress"
                percentageProgressPosition="left"
                mode="dark"
                progress={getStepCompletionPercentage(taskSteps)}
              />
            )}
          </AgxSpacer>
        }
        content={
          <AgxSpacer direction="horizontal" fill>
            <AgxSpacer direction="vertical" fill gap="spacing-2xl">
              {renderTaskContent ? renderTaskContent({ task, children: content }) : content}
            </AgxSpacer>
          </AgxSpacer>
        }
      />
      <TaskStepCompletionConfirmationModal
        isOpen={pendingStep !== undefined}
        taskLabel={getTaskLabel(task)}
        variant={pendingStep?.variant}
        onCancel={closePendingStepConfirmation}
        onConfirm={confirmPendingStep}
      />
    </div>
  );
};

export default TaskCollapsible;
